(() => {
  if (customElements.get("collection-price-sort")) return;
  const snapshots = new Map();
  const isPriceSort = value => value === "price-ascending" || value === "price-descending";
  const abortCheck = signal => {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  };
  async function fetchDocument(url, signal) {
    const response = await fetch(url, { signal, credentials: "same-origin" });
    if (!response.ok) throw new Error("Collection request failed");
    return new DOMParser().parseFromString(await response.text(), "text/html");
  }
  async function parallel(items, action, signal) {
    let index = 0;
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, async () => {
      while (index < items.length) {
        abortCheck(signal);
        const item = items[index++];
        await action(item);
      }
    }));
  }
  function rememberCards(snapshot, root) {
    root.querySelectorAll(".collection__results product-card[handle]").forEach(card => {
      snapshot.cards.set(card.getAttribute("handle"), card.outerHTML);
    });
  }

  class CollectionPriceSort extends HTMLElement {
    connectedCallback() {
      this.querySelector("button")?.addEventListener("click", () => this.refresh());
      if (isPriceSort(this.dataset.sort) && this.dataset.ready !== "true") {
        this.hidden = false;
        this.setBusy();
        // A directly opened/reloaded price-sort URL receives the same enhancement as an AJAX update.
        customElements.whenDefined("facets-form").then(() => {
          if (this.isConnected && this.dataset.ready !== "true") this.refresh();
        });
      }
    }
    refresh() {
      const url = new URL(location.href);
      url.searchParams.set("section_id", this.dataset.sectionId);
      this.dispatchEvent(new CustomEvent("facet:update", { bubbles: true, detail: { url, disableCache: true } }));
    }
    setBusy() {
      this.hidden = false;
      this.querySelector("[role=status]").textContent = this.dataset.loading;
      this.querySelector("button").hidden = true;
      this.closest(".shopify-section").querySelector(".collection__results")?.setAttribute("aria-busy", "true");
    }
    showError() {
      this.hidden = false;
      this.querySelector("[role=status]").textContent = this.dataset.error;
      this.querySelector("button").hidden = false;
      this.closest(".shopify-section").querySelector(".collection__results")?.removeAttribute("aria-busy");
    }
    static async prepare(document, url, signal, currentSection) {
      const controller = document.querySelector("collection-price-sort");
      if (!controller) return;
      controller.dataset.ready = "true";
      const sort = url.searchParams.get("sort_by") || controller.dataset.sort;
      if (!isPriceSort(sort)) return;
      const grid = document.querySelector(".collection__results product-list");
      if (!grid) return;
      currentSection.querySelector("collection-price-sort")?.setBusy();
      const pageSize = Number(controller.dataset.pageSize);
      const page = Math.max(1, Number(url.searchParams.get("page")) || 1);
      if (!Number.isInteger(pageSize) || pageSize < 1) throw new Error("Invalid collection page size");
      const canonical = new URL(url);
      canonical.hash = "";
      canonical.searchParams.delete("page");
      canonical.searchParams.set("sort_by", "price-ascending");
      canonical.searchParams.set("section_id", controller.dataset.sectionId);
      canonical.searchParams.sort();
      const context = [controller.dataset.country, controller.dataset.currency, controller.dataset.locale];
      const key = JSON.stringify([canonical.toString(), pageSize, ...context]);
      let snapshot = snapshots.get(key);
      if (!snapshot || Date.now() - snapshot.created > 60000) {
        const indexUrl = new URL(canonical);
        indexUrl.searchParams.set("section_id", "collection-price-index");
        indexUrl.searchParams.set("page", "1");
        const readIndex = async indexPage => {
          const requestUrl = new URL(indexUrl);
          requestUrl.searchParams.set("page", String(indexPage));
          const response = await fetchDocument(requestUrl, signal);
          const script = response.querySelector("[data-collection-price-index]");
          if (!script) throw new Error("Missing collection prices");
          const data = JSON.parse(script.textContent);
          if (JSON.stringify([data.country, data.currency, data.locale]) !== JSON.stringify(context)) throw new Error("Price context changed");
          return data;
        };
        const first = await readIndex(1);
        const indexPages = [first];
        await parallel(Array.from({ length: Math.max(0, first.pages - 1) }, (_, index) => index + 2), async indexPage => {
          indexPages[indexPage - 1] = await readIndex(indexPage);
        }, signal);
        const products = indexPages.flatMap(data => data.products);
        if (products.length !== first.total || new Set(products.map(p => p.handle)).size !== first.total || products.some(p => !Number.isFinite(p.price))) throw new Error("Incomplete price index");
        snapshot = { created: Date.now(), products, cards: new Map() };
        snapshots.set(key, snapshot);
        while (snapshots.size > 3) snapshots.delete(snapshots.keys().next().value);
      }
      rememberCards(snapshot, document);
      const direction = sort === "price-descending" ? -1 : 1;
      const sorted = [...snapshot.products].sort((a, b) => direction * (a.price - b.price) || a.position - b.position);
      const selected = sorted.slice((page - 1) * pageSize, page * pageSize);
      const neededPages = [...new Set(selected.filter(product => !snapshot.cards.has(product.handle)).map(product => Math.floor(product.position / pageSize) + 1))];
      await parallel(neededPages, async nativePage => {
        const pageUrl = new URL(canonical);
        pageUrl.searchParams.set("page", String(nativePage));
        rememberCards(snapshot, await fetchDocument(pageUrl, signal));
      }, signal);
      abortCheck(signal);
      const cards = selected.map(product => {
        const html = snapshot.cards.get(product.handle);
        if (!html) throw new Error("Missing product card");
        const template = document.createElement("template");
        template.innerHTML = html;
        const card = template.content.firstElementChild;
        card.dataset.displayedSortPrice = String(product.price);
        return card;
      });
      // Keep any configured promotional tiles in their existing slots.
      let cardIndex = 0;
      const children = Array.from(grid.children).flatMap(child => child.matches("product-card") ? (cards[cardIndex] ? [cards[cardIndex++]] : []) : [child]);
      children.push(...cards.slice(cardIndex));
      grid.replaceChildren(...children);
      controller.hidden = true;
      controller.dataset.total = String(sorted.length);
    }
  }
  customElements.define("collection-price-sort", CollectionPriceSort);
})();
